import { Component, ElementRef, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  UntypedFormArray,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { SwalService } from 'projects/shared/src/services/swal.service';

import Swal from 'sweetalert2';
import { Observable } from 'rxjs';
import { ProductEvaluationTaskService } from '../../../services/product-evaluation-task.service';
import { Criteria, CriteriasToEvaluateProduct } from '../../../models/product-evaluation-tasks/CriteriasToEvaluateProduct';
import { CriteriaAnswerTypeCode } from '../../../models/product-evaluation-tasks/enums/CriteriaAnswerTypeCodeEnum';

export interface DialogData {
  taskId: any;
  productId: any;
}

@Component({
  selector: 'app-product-evaluation-tasks-survey',
  standalone: false,
  templateUrl: './product-evaluation-tasks-survey.component.html'
})
export class ProductEvaluationTasksSurveyComponent implements OnInit {
  selectedIndex = 0;
  criteriaData: CriteriasToEvaluateProduct = new CriteriasToEvaluateProduct();
  isSubmitted = false;
  loading = false;
  fetching = false;
  criteriaAnswerTypeCode = CriteriaAnswerTypeCode;
  taskId!: number;

  formGroup: FormGroup = new FormGroup({});

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private dialogRef: MatDialogRef<ProductEvaluationTasksSurveyComponent>,
    private formBuilder: FormBuilder,
    private service: ProductEvaluationTaskService,
    private el: ElementRef,
    private translate: TranslateService,
    private swalService: SwalService
  ) { }

  ngOnInit() {
    this.taskId = this.data.taskId;
    this.buildForm();
    this.fetchQuestions();
  }

  fetchQuestions() {
    this.fetching = true;
    let params = {
      taskId: this.taskId,
      productId: this.data.productId
    };

    this.service.getCriteriasToEvaluateProduct(params).subscribe({
      next: (res: CriteriasToEvaluateProduct) => {
        this.criteriaData = res;
        this.bindData();
        this.fetching = false;
      },
      error: () => {
        this.fetching = false;
      }
    });
  }

  buildForm() {
    this.formGroup = this.formBuilder.group({
      items: this.formBuilder.array([])
    });
  }

  get items() {
    return this.formGroup.get('items') as UntypedFormArray;
  }

  bindData() {
    this.criteriaData.criterias?.forEach((criteria) => {
      this.addItem(criteria);
    });
  }

  addItem(criteria: Criteria) {
    const filesControl = this.formBuilder.control(criteria.attachmentUrls || []);

    const group: { [key: string]: any } = {
      questionDetails: [criteria],
      files: filesControl,
      deletedAttachmentIds: [[]]
    };

    switch (criteria.technicalCriteriaAnswerTypeCode) {
      case CriteriaAnswerTypeCode.Numeric:
        group['answer'] = [
          criteria.answerValue ?? null,
          [Validators.required
          ]
        ];
        break;

      case CriteriaAnswerTypeCode.Star_Rating:
        group['answer'] = [
          criteria.starRateEnum ? Number(criteria.starRateEnum) : null,
          [Validators.required]
        ];
        break;

      case CriteriaAnswerTypeCode.Percentage:
      default:
        group['answer'] = [
          criteria.answerValue ?? null,
          [Validators.required,
          Validators.min(0),
          Validators.max(100)
          ]
        ];
        break;
    }

    const itemGroup = this.formBuilder.group(group, {
      validators: this.questionValidator()
    });

    filesControl.valueChanges.subscribe(() => {
      itemGroup.get('answer')?.updateValueAndValidity({ emitEvent: false });
      itemGroup.updateValueAndValidity({ emitEvent: false, onlySelf: true });
    });

    this.items.push(itemGroup);
  }

  getQuestionDetails(item: AbstractControl): Criteria {
    return item.get('questionDetails')?.value;
  }

  isItemValid(item: AbstractControl): boolean {
    return item.valid;
  }

  onFilesSelected(item: AbstractControl, files: any[]) {
    item.get('files')?.setValue(files);
    item.get('files')?.markAsDirty();
    this.formGroup.markAsDirty();
    item.markAllAsTouched();
  }

  setStarValue(item: AbstractControl, star: any) {
    item.get('answer')?.setValue(star);
    item.get('answer')?.markAsDirty();
    item.get('answer')?.markAsTouched();
    this.formGroup.markAsDirty();
  }


  clearResponce(item: AbstractControl) {
    item.get('answer')?.setValue(null);
    item.get('answer')?.markAsDirty();
    this.formGroup.markAsDirty();
    item.markAllAsTouched();
  }

 deleteFile(item: AbstractControl, files: any[]) {
  const control = item.get('deletedAttachmentIds');
  if (control) {
    const currentValues = control.value || [];

    control.setValue([...currentValues, ...files]);
  }
}

  private buildAnswerFromItem(item: AbstractControl): any {
    const criteria: Criteria = this.getQuestionDetails(item);
    const rawAnswer = item.get('answer')?.value;

    const hasValidValue = rawAnswer !== null && rawAnswer !== undefined && String(rawAnswer).trim() !== '';

    const dto: any = {
      productEvaluationFormCriteriaId: criteria.id,
      answerValue: null,
      starRateEnum: null,
      deletedAttachmentIds: item.get('deletedAttachmentIds')?.value ?? []
    };

    switch (criteria.technicalCriteriaAnswerTypeCode) {
      case CriteriaAnswerTypeCode.Star_Rating:
        dto.starRateEnum = hasValidValue ? String(rawAnswer) : null;
        dto.answerValue = null;
        break;

      case CriteriaAnswerTypeCode.Numeric:
      case CriteriaAnswerTypeCode.Percentage:
      default:
        dto.answerValue = hasValidValue ? Number(rawAnswer) : null;
        dto.starRateEnum = null;
        break;
    }

    return dto;
  }

  private buildAnswersPayload(): any {
    return {
      taskId: this.taskId,
      productId: this.data.productId,
      answers: this.items.controls.map((item) => this.buildAnswerFromItem(item))
    };
  }

  appendAttachments(formData: FormData): void {
    this.items.controls.forEach((item) => {
      const criteria = this.getQuestionDetails(item);
      const files = item.get('files')?.value;

      if (files && files.length) {
        files.forEach((file: any) => {
          if (file instanceof File) {
            formData.append(`attachments_${criteria.id}`, file);
          }
        });
      }
    });
  }

  saveTaskDraftAnswers() {
    this.isSubmitted = true;
    const currentItem = this.items.at(this.selectedIndex);
    if (!currentItem || !currentItem.valid) {
      currentItem?.markAllAsTouched();
      this.scrollToFirstInvalidControl();
      return;
    }
    this.loading = true;
    const payload = this.buildAnswersPayload();
    const formData = new FormData();
    formData.append('data', JSON.stringify(payload));
    this.appendAttachments(formData);

    this.service.saveTaskAnswers(formData).subscribe({
      next: () => {
        this.loading = false;
        this.selectedIndex = this.selectedIndex + 1;
        this.isSubmitted = false;
        Swal.fire({
          title: this.translate.instant('swalMsg.savedsuccessfully'),
          imageUrl: './assets/images/icons/success.svg',
          confirmButtonText: this.translate.instant('swalMsg.Ok'),
        });
        this.dialogRef.close(true);
      },
      error: (error: any) => {
        this.loading = false;
        this.handleErrors(error?.detail);
      }
    });
  }

  saveRecord() {
    this.isSubmitted = true;

    if (this.formGroup.valid) {
      this.loading = true;
      const payload = this.buildAnswersPayload();
      const formData = new FormData();
      formData.append('data', JSON.stringify(payload));
      this.appendAttachments(formData);

      this.service.saveTaskAnswers(formData).subscribe({
        next: () => {
          this.loading = false;
          Swal.fire({
            title: this.translate.instant('productEvaluationTasks.productEvaluationDataSavedSuccessfully'),
            text: this.translate.instant('productEvaluationTasks.youCanNowContinueEvaluatingRemainingProducts'),
            imageUrl: './assets/images/icons/success.svg',
            confirmButtonText: this.translate.instant('swalMsg.Ok'),
          });
          this.dialogRef.close(true);
        },
        error: (error: any) => {
          this.loading = false;
          this.handleErrors(error?.detail);
        }
      });
    } else {
      this.formGroup.markAllAsTouched();
      this.scrollToFirstInvalidControl();
    }
  }

  handleErrors(errors: any) {
    if (!errors) return;
    try {
      const parsedErrors = typeof errors === 'string' ? JSON.parse(errors) : errors;
      parsedErrors?.ErrorDTO?.forEach((error: any) => {
        this.swalService.swalFailed(error.Error);
      });
    } catch (e) {
      this.swalService.swalFailed(errors);
    }
  }

  private scrollToFirstInvalidControl() {
    const firstInvalidControl: HTMLElement | null =
      this.el.nativeElement.querySelector('form .ng-invalid , form .has-error');
    firstInvalidControl?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest'
    });
  }

  handelSteps(index: number) {
    this.items.controls[index].markAllAsTouched();
    if (this.items.controls[index].valid) {
      this.selectedIndex = this.selectedIndex + 1;
    }
  }

  hasError(controlName: string, errorName: string, index: any = null): boolean {
    if (index !== null) {
      const itemGroup = this.items?.at(index) as FormGroup;
      if (!itemGroup || !itemGroup.controls) return false;

      const control = itemGroup.controls[controlName];
      return !!(control && control.touched && control.hasError(errorName));
    } else {
      const control = this.formGroup?.controls?.[controlName];
      return !!(control && control.hasError(errorName));
    }
  }

  questionValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const group = control as FormGroup;
      const criteria = group.get('questionDetails')?.value as Criteria;
      const filesControl = group.get('files');

      if (!criteria || !filesControl) {
        return null;
      }

      const hasAnswer = group.get('answer')?.value !== null && group.get('answer')?.value !== '';
      const files = filesControl.value as any[];
      const hasFiles = !!files?.length;

      const filesErrors = { ...(filesControl.errors || {}) };
      delete filesErrors['required'];

      if (criteria.isAttachmentsRequired && hasAnswer && !hasFiles) {
        filesErrors['required'] = true;
      }

      filesControl.setErrors(Object.keys(filesErrors).length ? filesErrors : null);

      return null;
    };
  }

  canDeactivate(): boolean | Observable<boolean> {
    return this.formGroup.dirty;
  }

  openConfirmDialog() {
    if (this.canDeactivate()) {
      return Swal.fire({
        title: this.translate.instant('swalMsg.saveProgressPrompt'),
        imageUrl: './assets/images/icons/warning-ic.svg',
        showConfirmButton: true,
        confirmButtonText: this.translate.instant('swalMsg.saveProgress'),
        showDenyButton: true,
        denyButtonText: this.translate.instant('swalMsg.continueExecution'),
        showCancelButton: true,
        cancelButtonText: this.translate.instant('swalMsg.exitWithoutSaving'),
        customClass: {
          actions: 'custom-swal-actions',
          confirmButton: 'custom-swal-btn custom-confirm-btn',
          denyButton: 'custom-swal-btn custom-deny-btn',
          cancelButton: 'custom-swal-btn custom-cancel-btn'
        }
      }).then((result) => {
        if (result.isConfirmed) {
          return this.saveTaskDraftAnswers();
        } else if (result.isDenied) {
          return false;
        } else {
          this.dialogRef.close();
        }
      });
    } else {
      this.dialogRef.close();
      return;
    }
  }
}
